import { memo } from 'react'

import { Box, Button, Checkbox, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'

import { useForm } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import SettingsGroup from 'modules/security_settings/components/SettingsGroup'
import { useUpdateSsoEnforcement } from 'modules/security_settings/pages/SecuritySettingsPage/SsoSection/useUpdateSsoEnforcement'
import { TypedSecuritySettingsSecuritySettingsPageFragment } from 'modules/security_settings/pages/SecuritySettingsPage/useCompanyConfig'

interface SsoEnforcementFormValues {
  ssoEnforcement: TypedSecuritySettingsSecuritySettingsPageFragment['ssoEnforcement']
}

interface Props {
  ssoEnforcement: TypedSecuritySettingsSecuritySettingsPageFragment['ssoEnforcement']
}

function SsoEnforcementForm({ ssoEnforcement }: Props) {
  const { updateSsoEnforcement, loading: updating } = useUpdateSsoEnforcement()

  const form = useForm<SsoEnforcementFormValues>({
    defaultValues: {
      ssoEnforcement,
    },
    onSubmit: async (data) => {
      const result = await updateSsoEnforcement(data.ssoEnforcement)
      const handled = handleResult(result, (_formErrors) => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('security_settings.sso.sso_enforcement.form.error_flash'),
          time: 60000, // 1 minute
        })
      })
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        const ssoEnforcement = handled.data.companyConfig?.ssoEnforcement
        if (ssoEnforcement) {
          form.resetForm({ values: { ssoEnforcement } })
        }
      }
      return handled
    },
  })

  const enforced = form.values.ssoEnforcement === 'domain' || form.values.ssoEnforcement === 'all'
  const domainOnly = form.values.ssoEnforcement === 'domain'

  function handleEnforcedChange(nextToggled: boolean) {
    if (nextToggled) {
      form.setFieldValue('ssoEnforcement', domainOnly ? 'domain' : 'all')
    } else {
      form.setFieldValue('ssoEnforcement', 'none')
    }
  }

  function handleDomainOnlyChange() {
    const nextToggled = !domainOnly
    form.setFieldValue('ssoEnforcement', nextToggled ? 'domain' : 'all')
  }

  return (
    <form onSubmit={form.handleSubmit}>
      <Box width="full" gap="s32">
        <SettingsGroup>
          <Box width="full" flexDirection="row" justifyContent="spaceBetween">
            <Text weight="semibold" size="300" color="grey1000">
              {i18n.t('security_settings.sso.sso_enforcement.form.enforce_sso.label')}
            </Text>
            <TogglerCheckbox value={enforced} onChange={handleEnforcedChange} disabled={updating} />
          </Box>
          <Checkbox
            checked={domainOnly}
            label={i18n.t('security_settings.sso.sso_enforcement.form.domain_only.label')}
            onChange={handleDomainOnlyChange}
            disabled={!enforced || updating}
          />
        </SettingsGroup>
        <Box width="full" flexDirection="rowReverse">
          <Button disabled={!form.dirty || updating} submit>
            {i18n.t('common.save_changes')}
          </Button>
        </Box>
      </Box>
    </form>
  )
}

export default memo(SsoEnforcementForm)
